// The note's page: emoji, title and blocks. Block J (UI-22d, 2026-10-11): restyled to
// mockups/notes.html on the tokens (notes.css), with the mockup's block gutter: + adds a block
// below, ⋮⋮ opens the block's menu (turn into, move up or down, duplicate, delete). Move up and
// down are the tap alternative to reordering. Fixed with it, each failing first in Notes.test.js:
//   - the first render was reported to the parent as an edit, so opening a note saved it;
//   - the format toolbar added the page scroll to a position: fixed top (off screen once scrolled);
//   - Tab typed spaces in every block, so the keyboard could never leave the editor (only a code
//     block indents now).
// Carla's decisions 1 and 4 (2026-10-12), failing first in NotesPages.test.js:
//   - a chosen picture goes to the private file store (uploadImage) and the block keeps only its
//     file id; it is shown by a short-lived signed link fetched when shown, and fetched again when
//     it expires. A picture stored inside an older note (a data: URL in src) still shows as it is:
//     nothing rewrites old notes; "Change" on it moves that picture to the file store;
//   - a picture's data: URL was copied into the note's text as "[Image: data:...]", so an inline
//     picture was sent twice in every save;
//   - "Page inside" (onCreatePage) makes a page inside this one and leaves a link block to it.
import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { Menu } from "../../ui";
import { messageFromError } from "../../utils/apiError";
import "./notes.css";

// ── Helpers ───────────────────────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 10); }

// A new note's id (the server takes the client's id). Never the same twice in one session, even
// for two pages made in the same millisecond.
let lastNoteId = 0;
export function newNoteId() {
  let t = Date.now();
  if (t <= lastNoteId) t = lastNoteId + 1;
  lastNoteId = t;
  return String(t);
}

export function makeBlock(type = 'paragraph', html = '', extra = {}) {
  return { id: uid(), type, html, checked: false, src: '', alt: '', ...extra };
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function stripHtml(html) {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  return d.textContent || '';
}

export function textToBlocks(text) {
  if (!text || !text.trim()) return [makeBlock()];
  return text.split('\n').map(l => makeBlock('paragraph', escapeHtml(l)));
}

export function blocksToText(blocks) {
  return (blocks || []).map(b => {
    if (b.type === 'divider') return '---';
    // Never the picture's address: an inline data: URL here doubled every save of the note.
    if (b.type === 'image') return b.alt ? `[Image: ${b.alt}]` : '[Image]';
    if (b.type === 'page') return '';
    const prefix =
      b.type === 'checkbox' ? (b.checked ? '[x] ' : '[ ] ') :
        b.type === 'bullet' ? '• ' :
          b.type === 'numbered' ? '1. ' : '';
    return prefix + stripHtml(b.html);
  }).filter(Boolean).join('\n');
}

// ── Block types (the slash menu and "Turn into") ─────────────────────────────
export const SLASH_CMDS = [
  { type: 'h1', icon: 'H1', label: 'Heading', desc: 'Big section title' },
  { type: 'h2', icon: 'H2', label: 'Subheading', desc: 'Smaller title' },
  { type: 'h3', icon: 'H3', label: 'Small heading', desc: 'A label for a section' },
  { type: 'paragraph', icon: 'T', label: 'Text', desc: 'Plain writing' },
  { type: 'checkbox', icon: '☑', label: 'To-do', desc: 'Tick it off' },
  { type: 'bullet', icon: '•', label: 'Bullet list', desc: 'Simple list' },
  { type: 'numbered', icon: '1.', label: 'Numbered list', desc: 'A list in order' },
  { type: 'callout', icon: '💡', label: 'Callout', desc: 'Make it stand out' },
  { type: 'quote', icon: '❝', label: 'Quote', desc: 'Capture a quote' },
  { type: 'code', icon: '<>', label: 'Code', desc: 'Monospace text' },
  { type: 'divider', icon: '—', label: 'Divider', desc: 'Split sections' },
  { type: 'image', icon: '🖼', label: 'Image', desc: 'Upload a picture' },
];
// Offered only where the page can make one (the Notes page passes onCreatePage).
const PAGE_CMD = { type: 'page', icon: '📄', label: 'Page inside', desc: 'Nest a new page here' };

// ── Emoji list ─────────────────────────────────────────────────────────────────
const EMOJIS = [
  '📝', '📄', '💼', '📞', '🤝', '📅', '📊', '📈', '💡', '📌', '📎', '✅', '🏡', '🍝', '📚', '✈️',
  '🎁', '🧸', '🛒', '💰', '💎', '⚡', '🌈', '☀️', '🌊', '❄️', '🍎', '☕', '🚀', '🎮', '🎵', '🎬',
  '📱', '💻', '🔑', '🔒', '⭐', '✨', '🔥', '🎉', '🏆', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤',
  '😀', '😊', '🥰', '😎', '🥳', '🤔', '😅', '🙌', '👍', '👏', '💪', '🙏', '👋', '🤖', '👻', '⚠️',
];

// ── Format toolbar ──────────────────────────────────────────────────────────
// position: fixed, so the selection's on-screen rect is used as it is: adding window.scrollY (as
// before) put it off screen once the page had scrolled.
const TOOLBAR_W = 300;
export function FormatToolbar({ rect, onFormat, onLink, onClose }) {
  const [linkMode, setLinkMode] = useState(false);
  const [url, setUrl] = useState('https://');
  const ref = useRef();

  useEffect(() => {
    function onMouseDown(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onClose]);

  const vw = typeof window !== 'undefined' ? window.innerWidth || 1024 : 1024;
  const top = rect.top >= 56 ? rect.top - 48 : (rect.bottom || rect.top) + 8;
  const left = Math.max(8, Math.min(rect.left, vw - TOOLBAR_W - 8));

  const btn = (cmd, label, title, value) => (
    <button key={cmd} type="button" title={title} aria-label={title} className="nt-fmt__btn"
      onMouseDown={e => { e.preventDefault(); onFormat(cmd, value); }}>{label}</button>
  );

  return (
    <div ref={ref} className="nt-fmt" role="toolbar" aria-label="Format text" style={{ position: 'fixed', top, left }}>
      {!linkMode ? (
        <>
          {btn('bold', <b>B</b>, 'Bold')}
          {btn('italic', <i>I</i>, 'Italic')}
          {btn('underline', <u>U</u>, 'Underline')}
          {btn('strikeThrough', <s>S</s>, 'Strikethrough')}
          <span className="nt-fmt__sep" aria-hidden="true" />
          <button type="button" title="Link" aria-label="Link" className="nt-fmt__btn" onMouseDown={e => { e.preventDefault(); setLinkMode(true); }}>🔗</button>
          {btn('unlink', '✂️', 'Remove link')}
          <span className="nt-fmt__sep" aria-hidden="true" />
          {btn('hiliteColor', '🖊', 'Highlight', 'var(--highlight)')}
          {btn('removeFormat', 'Clear', 'Clear formatting')}
        </>
      ) : (
        <>
          <input
            autoFocus
            className="nt-fmt__input"
            aria-label="Link address"
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { onLink(url); setLinkMode(false); }
              if (e.key === 'Escape') setLinkMode(false);
            }}
            placeholder="https://..."
          />
          <button type="button" className="nt-fmt__btn nt-fmt__btn--apply" onMouseDown={e => { e.preventDefault(); onLink(url); setLinkMode(false); }}>Apply</button>
          <button type="button" className="nt-fmt__btn" aria-label="Cancel link" onMouseDown={e => { e.preventDefault(); setLinkMode(false); }}>✕</button>
        </>
      )}
    </div>
  );
}

// ── Slash command menu ────────────────────────────────────────────────────────
const SLASH_W = 280;
const SLASH_H = 340;
function SlashMenu({ x, y, query, onSelect, onClose, cmds = SLASH_CMDS }) {
  const [sel, setSel] = useState(0);

  const matches = cmds.filter(c =>
    !query || c.label.toLowerCase().includes(query.toLowerCase()) || c.type.includes(query.toLowerCase())
  );

  useEffect(() => { setSel(0); }, [query]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, matches.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
      if (e.key === 'Enter') { e.preventDefault(); if (matches[sel]) onSelect(matches[sel].type); }
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [matches, sel, onSelect, onClose]);

  if (!matches.length) return null;

  // Kept on screen: a phone is narrower than x + the menu, and the last line sits near the bottom.
  const vw = window.innerWidth || 1024;
  const vh = window.innerHeight || 768;
  const left = Math.max(8, Math.min(x, vw - SLASH_W - 8));
  const top = Math.max(8, Math.min(y, vh - Math.min(SLASH_H, vh - 16) - 8));

  return (
    <div className="nt-slash" role="listbox" aria-label="Add a block" style={{ position: 'fixed', left, top }}>
      <span className="nt-slash__title">Add a block</span>
      {matches.map((c, i) => (
        <div
          key={c.type}
          role="option"
          aria-selected={i === sel}
          className="nt-slash__item"
          onMouseDown={e => { e.preventDefault(); onSelect(c.type); }}
        >
          <span className="nt-slash__icon" aria-hidden="true">{c.icon}</span>
          <span className="nt-slash__text">
            <span className="nt-slash__label">{c.label}</span>
            <span className="nt-slash__desc">{c.desc}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Emoji picker ──────────────────────────────────────────────────────────────
function EmojiPicker({ onPick, onClose }) {
  const ref = useRef();
  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  return (
    <div ref={ref} className="nt-emoji" role="dialog" aria-label="Choose an icon">
      {EMOJIS.map(e => (
        <button key={e} type="button" className="nt-emoji__btn" onClick={() => onPick(e)} aria-label={`Use ${e}`}>{e}</button>
      ))}
      <button type="button" className="nt-emoji__clear" onClick={() => onPick('')}>No icon</button>
    </div>
  );
}

// ── Single block renderer ─────────────────────────────────────────────────────
// A picture in the file store: a signed link fetched when shown, fetched again before it expires
// and when the browser says the image failed (an expired link). A failure says so, with Try again.
export function NoteImage({ fileId, alt, imageLink }) {
  const [state, setState] = useState({ url: null, expiresIn: 0, error: null });
  const retries = useRef(0);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const load = useCallback(async () => {
    try {
      if (!imageLink) throw new Error('This picture cannot be shown here.');
      const { url, expiresIn } = await imageLink(fileId);
      if (alive.current) setState({ url, expiresIn: Number(expiresIn) || 0, error: null });
    } catch (err) {
      if (alive.current) setState({ url: null, expiresIn: 0, error: messageFromError(err, 'This picture could not be shown.') });
    }
  }, [fileId, imageLink]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!state.url || !state.expiresIn) return undefined;
    const t = setTimeout(() => { retries.current = 0; load(); }, Math.max(30, state.expiresIn - 30) * 1000);
    return () => clearTimeout(t);
  }, [state.url, state.expiresIn, load]);
  if (state.error) {
    return (
      <p className="nt-failed nt-b-image__failed" role="alert">
        {state.error}{' '}
        <button type="button" className="ui-btn ui-btn--tertiary ui-btn--sm" onClick={() => { setState(s => ({ ...s, error: null })); load(); }}>Try again</button>
      </p>
    );
  }
  if (!state.url) return <div className="nt-b-image__loading" aria-live="polite">Loading the picture…</div>;
  return (
    <img src={state.url} alt={alt || 'Image'}
      onError={() => { if (retries.current < 2) { retries.current += 1; load(); } }} />
  );
}

function Block({ block, blockRef, onKeyDown, onInput, onChange, onImageUpload, placeholder, imageLink, imageState, pageInfo, onOpenPage }) {
  if (block.type === 'divider') {
    return <div className="nt-b-divider" role="separator"><span /></div>;
  }

  if (block.type === 'page') {
    const info = pageInfo ? pageInfo(block.pageId) : null;
    return info ? (
      <button type="button" className="nt-b-page" aria-label={`Open page: ${info.title}`} onClick={() => onOpenPage?.(block.pageId)}>
        <span aria-hidden="true">{info.emoji || '📄'}</span> {info.title}
      </button>
    ) : (
      <span className="nt-b-page nt-b-page--gone"><span aria-hidden="true">📄</span> A page that is no longer here</span>
    );
  }

  if (block.type === 'image') {
    const busy = imageState?.busy;
    const failed = imageState?.error;
    return (
      <div className="nt-b-image">
        {busy ? (
          <div className="nt-b-image__loading" aria-live="polite">Saving the picture…</div>
        ) : (block.fileId || block.src) ? (
          <div className="nt-b-image__frame">
            {block.fileId
              ? <NoteImage fileId={block.fileId} alt={block.alt} imageLink={imageLink} />
              : <img src={block.src} alt={block.alt || 'Image'} />}
            <input
              className="nt-b-image__caption"
              aria-label="Caption"
              placeholder="Add a caption"
              value={block.alt || ''}
              onChange={e => onChange({ ...block, alt: e.target.value })}
            />
            <button type="button" className="ui-btn ui-btn--tertiary ui-btn--sm nt-b-image__change" onClick={() => onImageUpload(block.id)}>Change</button>
          </div>
        ) : (
          <button type="button" className="nt-b-image__empty" onClick={() => onImageUpload(block.id)}>
            🖼 Choose an image
          </button>
        )}
        {failed && <p className="nt-failed" role="alert">{failed}</p>}
      </div>
    );
  }

  const editable = (extraClass, ph) => (
    <div
      ref={blockRef}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={ph}
      data-block-id={block.id}
      onKeyDown={e => onKeyDown(e, block.id)}
      onInput={e => onInput(e, block.id)}
      data-placeholder={placeholder || ph}
      className={`nt-ce ${extraClass}`}
      dangerouslySetInnerHTML={{ __html: block.html }}
    />
  );

  if (block.type === 'checkbox') {
    return (
      <div className={`nt-b-todo${block.checked ? ' nt-b-todo--done' : ''}`}>
        <input
          type="checkbox"
          className="nt-check"
          aria-label={block.checked ? 'Done' : 'Not done'}
          checked={!!block.checked}
          onChange={e => onChange({ ...block, checked: e.target.checked })}
        />
        {editable('nt-ce--todo', 'To-do')}
      </div>
    );
  }

  if (block.type === 'callout') {
    return (
      <div className="nt-b-callout">
        <span aria-hidden="true">💡</span>
        {editable('nt-ce--callout', 'Callout')}
      </div>
    );
  }

  if (block.type === 'bullet' || block.type === 'numbered') {
    return (
      <div className="nt-b-list">
        <span className="nt-b-list__mark" aria-hidden="true">{block.type === 'bullet' ? '•' : '1.'}</span>
        {editable('nt-ce--list', 'List item')}
      </div>
    );
  }

  const PH = { h1: 'Heading', h2: 'Subheading', h3: 'Small heading', quote: 'Quote', code: 'Code', paragraph: "Type '/' for blocks" };
  const type = PH[block.type] ? block.type : 'paragraph';
  return editable(`nt-ce--${type}`, PH[type]);
}

// ── Main BlockEditor ──────────────────────────────────────────────────────────
// meta: rendered between the title and the blocks (the page's category chips and "Edited …").
// uploadImage(file) -> Promise<fileId>; imageLink(fileId) -> Promise<{ url, expiresIn }>;
// onCreatePage(noteWithLinkBlock, pageId); pageInfo(pageId) -> { title, emoji } | null; onOpenPage(id).
// Decision 90 v2: autoFocusTitle puts the cursor in the title (a new note); pageRequest, a number the
// page raises, adds a page inside at the end ("Add page inside" in the editor's bar), the same way the
// "Page inside" block does, so what has been typed is carried with it.
export default function BlockEditor({ note, onChange, meta, uploadImage, imageLink, onCreatePage, pageInfo, onOpenPage, autoFocusTitle = false, pageRequest = 0 }) {
  const initialBlocks = note.blocks?.length
    ? note.blocks
    : textToBlocks(note.text);

  const [blocks, setBlocks] = useState(initialBlocks);
  const [title, setTitle] = useState(note.title || '');
  const [emoji, setEmoji] = useState(note.emoji || '');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [slash, setSlash] = useState(null); // { blockId, query, x, y }
  const [fmtBar, setFmtBar] = useState(null); // { rect }
  const [imageState, setImageState] = useState({}); // blockId → { busy, error }

  const blockRefs = useRef({});    // blockId → DOM element
  const pendingHtml = useRef({});   // blockId → current innerHTML (not yet in state)
  const imgInputs = useRef({});   // blockId → hidden file input
  const firstRun = useRef(true);
  const titleRef = useRef(null);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [title]);

  // Sync to parent whenever blocks / title / emoji change. Not on the first render: opening a
  // note is not an edit (it used to save the note, and move it to the top as "just edited").
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    const text = blocksToText(blocks);
    onChange({ ...note, title, emoji, blocks, text });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, title, emoji]);

  const setBlockRef = useCallback((id, el) => {
    if (el) blockRefs.current[id] = el;
  }, []);

  // ── helpers ──────────────────────────────────────────────────────────────────
  // A block whose html has not changed is left as it is, so a blur alone is not an edit.
  function syncBlock(id) {
    const el = blockRefs.current[id];
    if (!el) return;
    const html = el.innerHTML;
    pendingHtml.current[id] = html;
    setBlocks(bs => (bs.some(b => b.id === id && b.html !== html)
      ? bs.map(b => (b.id === id ? { ...b, html } : b))
      : bs));
  }

  function flushAll() {
    setBlocks(bs => {
      let changed = false;
      const next = bs.map(b => {
        const el = blockRefs.current[b.id];
        if (!el || el.innerHTML === b.html) return b;
        changed = true;
        return { ...b, html: el.innerHTML };
      });
      return changed ? next : bs;
    });
  }

  function focusBlock(id, atEnd = false) {
    setTimeout(() => {
      const el = blockRefs.current[id];
      if (el) { el.focus(); if (atEnd) placeCursorAtEnd(el); else placeCursorAtStart(el); }
    }, 0);
  }

  function insertAfter(afterId, type = 'paragraph', { openSlash = false } = {}) {
    flushAll();
    const fresh = makeBlock(type);
    setBlocks(bs => {
      const idx = bs.findIndex(b => b.id === afterId);
      const next = [...bs];
      next.splice(idx + 1, 0, fresh);
      return next;
    });
    setTimeout(() => {
      const el = blockRefs.current[fresh.id];
      if (!el) return;
      el.focus();
      placeCursorAtStart(el);
      if (openSlash) {
        const rect = el.getBoundingClientRect();
        setSlash({ blockId: fresh.id, query: '', x: rect.left, y: rect.bottom + 4 });
      }
    }, 0);
  }

  function deleteBlock(id) {
    flushAll();
    setBlocks(bs => {
      if (bs.length <= 1) return [makeBlock()];
      const idx = bs.findIndex(b => b.id === id);
      const prevId = idx > 0 ? bs[idx - 1].id : null;
      if (prevId) focusBlock(prevId, true);
      return bs.filter(b => b.id !== id);
    });
  }

  function moveBlock(id, delta) {
    flushAll();
    setBlocks(bs => {
      const i = bs.findIndex(b => b.id === id);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= bs.length) return bs;
      const next = [...bs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  function duplicateBlock(id) {
    const el = blockRefs.current[id];
    setBlocks(bs => {
      const i = bs.findIndex(b => b.id === id);
      if (i < 0) return bs;
      const src = { ...bs[i], html: el ? el.innerHTML : bs[i].html };
      const next = [...bs];
      next.splice(i + 1, 0, { ...src, id: uid() });
      return next;
    });
  }

  function changeType(id, newType) {
    // special: divider & image have no html content
    if (newType === 'divider' || newType === 'image') {
      setBlocks(bs => bs.map(b => b.id === id ? { ...b, type: newType, html: '' } : b));
      if (newType === 'image') handleImageUpload(id);
      return;
    }
    const el = blockRefs.current[id];
    const html = el ? el.innerHTML : (pendingHtml.current[id] || '');
    setBlocks(bs => bs.map(b => b.id === id ? { ...b, type: newType, html } : b));
    focusBlock(id, true);
  }

  // ── keyboard events ──────────────────────────────────────────────────────────
  function handleKeyDown(e, id) {
    const el = blockRefs.current[id];
    const block = blocks.find(b => b.id === id);

    if (e.key === 'Enter' && !e.shiftKey) {
      // If slash menu open, let it handle via its own keydown listener
      if (slash) return;
      if (block?.type === 'code') return;   // a new line inside the code block
      e.preventDefault();

      const listTypes = ['bullet', 'numbered', 'checkbox'];
      const isList = block && listTypes.includes(block.type);

      // Empty list item → exit the list (convert to paragraph)
      if (isList && el && !el.textContent.trim()) {
        changeType(id, 'paragraph');
        return;
      }

      // Split: content before cursor stays, content after goes into new block
      const sel = window.getSelection();
      let afterHtml = '';
      if (sel && sel.rangeCount && el) {
        const range = sel.getRangeAt(0);
        const afterRange = range.cloneRange();
        afterRange.setEnd(el, el.childNodes.length);
        const frag = afterRange.extractContents();
        const div = document.createElement('div');
        div.appendChild(frag);
        afterHtml = div.innerHTML;
        afterRange.deleteContents();
      }
      syncBlock(id);
      // Continue same type for list blocks, otherwise paragraph
      const fresh = makeBlock(isList ? block.type : 'paragraph', afterHtml);
      setBlocks(bs => {
        const idx = bs.findIndex(b => b.id === id);
        const arr = [...bs];
        arr.splice(idx + 1, 0, fresh);
        return arr;
      });
      focusBlock(fresh.id);
    }

    if (e.key === 'Backspace') {
      if (slash) return;
      const isEmpty = !el || el.textContent.trim() === '';
      if (block?.type !== 'paragraph' && isEmpty) {
        e.preventDefault();
        changeType(id, 'paragraph');
        return;
      }
      if (isEmpty && blocks.length > 1) {
        e.preventDefault();
        deleteBlock(id);
      }
    }

    // Only a code block indents; everywhere else Tab moves on, so the keyboard is never trapped.
    if (e.key === 'Tab' && !e.shiftKey && block?.type === 'code') {
      e.preventDefault();
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;');
    }

    if (e.key === 'Escape' && slash) {
      setSlash(null);
    }
  }

  // While typing, the DOM is ahead of state (state only catches up on blur, because re-rendering a
  // contentEditable moves the caret). So the parent hears the live text after a pause in typing,
  // read from the DOM: a long stretch of writing is saved without first clicking away.
  const liveTimer = useRef(null);
  const latest = useRef({ blocks, title, emoji, note, onChange });
  latest.current = { blocks, title, emoji, note, onChange };
  function sendLive() {
    liveTimer.current = null;
    const cur = latest.current;
    const live = cur.blocks.map(b => {
      const el = blockRefs.current[b.id];
      return el && el.innerHTML !== b.html ? { ...b, html: el.innerHTML } : b;
    });
      cur.onChange({ ...cur.note, title: cur.title, emoji: cur.emoji, blocks: live, text: blocksToText(live) });
  }
  function reportLive() {
    clearTimeout(liveTimer.current);
    liveTimer.current = setTimeout(sendLive, 700);
  }
  // Closing the note inside the pause still reports what was typed.
  useEffect(() => () => { if (liveTimer.current) { clearTimeout(liveTimer.current); sendLive(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleInput(e, id) {
    const el = blockRefs.current[id];
    if (!el) return;
    pendingHtml.current[id] = el.innerHTML;
    reportLive();

    const text = el.textContent || '';

    // Show/update slash menu
    if (text.startsWith('/')) {
      const query = text.slice(1);
      const rect = el.getBoundingClientRect();
      setSlash({ blockId: id, query, x: rect.left, y: rect.bottom + 4 });
    } else if (slash?.blockId === id) {
      setSlash(null);
    }
  }

  function handleSlashSelect(type) {
    const id = slash.blockId;
    setSlash(null);
    const el = blockRefs.current[id];
    if (el) el.innerHTML = '';
    pendingHtml.current[id] = '';
    if (type === 'page') { createPage(id); return; }
    setBlocks(bs => bs.map(b => b.id === id ? { ...b, type, html: '' } : b));
    if (type !== 'divider' && type !== 'image') focusBlock(id, true);
    if (type === 'image') handleImageUpload(id);
  }

  // ── selection → format toolbar ───────────────────────────────────────────────
  useEffect(() => {
    function handleSelectionChange() {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.rangeCount || !sel.toString().trim()) {
        setFmtBar(null);
        return;
      }
      const node = sel.anchorNode;
      const anchor = node && (node.nodeType === 1 ? node : node.parentElement);
      if (!anchor || !anchor.closest || !anchor.closest('.nt-ce')) return;
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (rect.width === 0) return;
      setFmtBar({ rect });
    }
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  function execFormat(cmd, value) {
    // The highlight takes the token's real colour (execCommand cannot read a CSS variable).
    const v = value === 'var(--highlight)'
      ? getComputedStyle(document.documentElement).getPropertyValue('--highlight').trim() || value
      : value;
    document.execCommand(cmd, false, v || null);
  }

  function insertLink(url) {
    if (!url) return;
    document.execCommand('createLink', false, url);
    // Make links open in new tab
    setTimeout(() => {
      document.querySelectorAll('.nt-ce a[href]').forEach(a => { a.target = '_blank'; a.rel = 'noopener noreferrer'; });
    }, 0);
  }

  // ── image upload ──────────────────────────────────────────────────────────────
  function handleImageUpload(blockId) {
    setTimeout(() => {
      const inp = imgInputs.current[blockId];
      if (inp) inp.click();
    }, 0);
  }

  // The picture goes to the file store; the block keeps only its file id (never the bytes, never
  // a data: URL, never a link). A refusal is said under the picture's own block.
  async function handleImageChange(blockId, file) {
    if (!file) return;
    if (!uploadImage) {
      setImageState(s => ({ ...s, [blockId]: { busy: false, error: 'Pictures cannot be added here.' } }));
      return;
    }
    setImageState(s => ({ ...s, [blockId]: { busy: true, error: null } }));
    try {
      const fileId = await uploadImage(file);
      setBlocks(bs => bs.map(b => (b.id === blockId ? { ...b, type: 'image', fileId, src: '' } : b)));
      setImageState(s => ({ ...s, [blockId]: { busy: false, error: null } }));
    } catch (err) {
      setImageState(s => ({ ...s, [blockId]: { busy: false, error: messageFromError(err, 'That picture was not saved. Try again.') } }));
    }
  }

  // "Page inside": this block becomes a link to a new page inside this one. The page is told the
  // note as it stands (with the link) and the new page's id in one call, so the link is saved even
  // though opening the new page closes this editor straight away.
  function createPage(blockId) {
    if (!onCreatePage) return;
    const pageId = newNoteId();
    const live = blocks.map(b => {
      const el = blockRefs.current[b.id];
      return el && el.innerHTML !== b.html ? { ...b, html: el.innerHTML } : b;
    });
    const next = live.map(b => (b.id === blockId ? { ...b, type: 'page', html: '', pageId } : b));
    clearTimeout(liveTimer.current);
    liveTimer.current = null;
    setBlocks(next);
    onCreatePage({ ...note, title, emoji, blocks: next, text: blocksToText(next) }, pageId);
  }

  // "Add page inside" from the bar: a new last block that becomes the link (createPage above).
  const lastRequest = useRef(pageRequest);
  useEffect(() => {
    if (!pageRequest || pageRequest === lastRequest.current) return;
    lastRequest.current = pageRequest;
    if (!onCreatePage) return;
    const pageId = newNoteId();
    const live = blocks.map(b => {
      const el = blockRefs.current[b.id];
      return el && el.innerHTML !== b.html ? { ...b, html: el.innerHTML } : b;
    });
    // An empty last line is used rather than left above the link.
    const last = live[live.length - 1];
    const reuse = last && last.type === 'paragraph' && !stripHtml(last.html).trim();
    const link = makeBlock('page', '', { pageId });
    const next = reuse ? [...live.slice(0, -1), { ...link, id: last.id }] : [...live, link];
    clearTimeout(liveTimer.current);
    liveTimer.current = null;
    setBlocks(next);
    onCreatePage({ ...note, title, emoji, blocks: next, text: blocksToText(next) }, pageId);
  }, [pageRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  const typeLabel = (t) => [...SLASH_CMDS, PAGE_CMD].find(c => c.type === t)?.label || 'Text';

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <div className="nt-page">
      <div className="nt-page__icon">
        <button
          type="button"
          className="nt-icon-tile"
          onClick={() => setShowEmojiPicker(p => !p)}
          aria-label={emoji ? 'Change icon' : 'Add an icon'}
          aria-expanded={showEmojiPicker}
        ><span className={emoji ? undefined : 'nt-icon-tile__empty'}>{emoji || '📄'}</span></button>
        {showEmojiPicker && (
          <EmojiPicker
            onPick={e => { setEmoji(e); setShowEmojiPicker(false); }}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </div>

      {/* A textarea that grows, so a long title wraps instead of being cut off; Enter moves to
          the first block rather than typing a new line. */}
      <textarea
        ref={titleRef}
        rows={1}
        className="nt-title"
        aria-label="Note title"
        value={title}
        onChange={e => setTitle(e.target.value.replace(/\n/g, ' '))}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (blocks[0]) focusBlock(blocks[0].id); } }}
        placeholder="Untitled"
        autoFocus={autoFocusTitle}
      />

      {meta}

      {fmtBar && (
        <FormatToolbar
          rect={fmtBar.rect}
          onFormat={execFormat}
          onLink={insertLink}
          onClose={() => setFmtBar(null)}
        />
      )}

      <div className="nt-blocks">
        {blocks.map((block, idx) => {
          const pl = block.type === 'paragraph' && idx === 0 && !block.html && blocks.length === 1
            ? "Start writing, or type '/' for blocks"
            : undefined;
          const items = [
            { label: 'Add a block below', icon: '+', onSelect: () => insertAfter(block.id, 'paragraph', { openSlash: true }) },
            ...(idx > 0 ? [{ label: 'Move up', icon: '↑', onSelect: () => moveBlock(block.id, -1) }] : []),
            ...(idx < blocks.length - 1 ? [{ label: 'Move down', icon: '↓', onSelect: () => moveBlock(block.id, 1) }] : []),
            { label: 'Duplicate', icon: '⧉', onSelect: () => duplicateBlock(block.id) },
            ...SLASH_CMDS.filter(c => c.type !== block.type && block.type !== 'page').map(c => ({
              label: `Turn into ${c.label.toLowerCase()}`, icon: c.icon, onSelect: () => changeType(block.id, c.type),
            })),
            { label: 'Delete block', icon: '🗑', danger: true, onSelect: () => deleteBlock(block.id) },
          ];
          return (
            <div
              key={block.id}
              className={`nt-row nt-row--${block.type}`}
              onBlur={() => { syncBlock(block.id); }}
            >
              <div className="nt-row__gutter">
                <button type="button" className="nt-gutter-btn nt-gutter-btn--add ui-hit" aria-label="Add a block below"
                  onClick={() => insertAfter(block.id, 'paragraph', { openSlash: true })}>+</button>
                <Menu items={items} label={`Block options (${typeLabel(block.type)})`} title="Block options"
                  buttonClassName="nt-gutter-btn ui-hit" align="left">
                  <span aria-hidden="true">⋮⋮</span>
                  <span className="ui-visually-hidden">{`Block options (${typeLabel(block.type)})`}</span>
                </Menu>
              </div>
              <div className="nt-row__body">
                <Block
                  block={block}
                  blockRef={el => setBlockRef(block.id, el)}
                  onKeyDown={handleKeyDown}
                  onInput={handleInput}
                  onChange={updated => setBlocks(bs => bs.map(b => b.id === updated.id ? updated : b))}
                  onImageUpload={handleImageUpload}
                  placeholder={pl}
                  imageLink={imageLink}
                  imageState={imageState[block.id]}
                  pageInfo={pageInfo}
                  onOpenPage={onOpenPage}
                />
              </div>
              {block.type === 'image' && (
                <input
                  key={`img-${block.id}`}
                  ref={el => { if (el) imgInputs.current[block.id] = el; }}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  hidden
                  aria-label="Choose an image"
                  onChange={e => handleImageChange(block.id, e.target.files?.[0])}
                />
              )}
            </div>
          );
        })}

        <button
          type="button"
          className="nt-add-block"
          onClick={() => insertAfter(blocks[blocks.length - 1]?.id || '', 'paragraph', { openSlash: true })}
        >
          + Add a block
        </button>
      </div>

      {slash && (
        <SlashMenu
          x={slash.x} y={slash.y}
          query={slash.query}
          cmds={onCreatePage ? [...SLASH_CMDS, PAGE_CMD] : SLASH_CMDS}
          onSelect={handleSlashSelect}
          onClose={() => setSlash(null)}
        />
      )}
    </div>
  );
}

// ── Cursor utils ──────────────────────────────────────────────────────────────
function placeCursorAtEnd(el) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

function placeCursorAtStart(el) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}
